import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { fbt } from 'fbtee'
import { useAuth } from '../hooks/useAuth'
import { useGuestLandingSession } from '../hooks/useGuestLandingSession'
import { useTotemFollows } from '../hooks/useTotemFollows'
import { useTotemLanding } from '../hooks/useTotemLanding'
import { setPendingIntent } from '../lib/pendingIntent'
import { pageTitle, useDocumentTitle } from '../hooks/useDocumentTitle'
import { useTransitionNavigate } from '../lib/viewTransition'
import { recordTrace, useTraceStart } from '../lib/perf'
import { toastError } from '../lib/toast'
import { classifyError } from '../lib/errors'
import { Button } from '../components/ui/button'
import { TotemAvatar } from '../components/TotemAvatar'
import type { Totem } from '../types'
import { backToHomeButtonLabel } from '../lib/commonStrings'
import { LinkLandingMessage, LinkLandingSpinner } from '../components/LinkLandingState'

type Status = 'resolving' | 'no-route' | 'invalid'

// Where a totem's QR code lands. Not a view — it follows the totem, works out
// which festival it's currently at, and hands off to the totem's route view in
// festival mode. The scanner is typically a stranger standing in a crowd, so
// there's no confirmation step: following is one tap to undo on the far side,
// and a prompt between the scan and the answer is friction at the worst moment.
export default function FollowTotemPage() {
  const { groupId, totemId } = useParams<{ groupId: string; totemId: string }>()
  const { user } = useAuth()
  const navigate = useTransitionNavigate()
  const { followTotem } = useTotemFollows(user?.uid ?? null)
  const { loadTotem, resolveLiveEvent } = useTotemLanding()
  const [status, setStatus] = useState<Status>('resolving')
  const [totem, setTotem] = useState<Totem | null>(null)
  const resolvedFor = useRef<string | null>(null)
  const traceStart = useTraceStart()

  useDocumentTitle(
    totem
      ? pageTitle(totem.name)
      : pageTitle(fbt('Follow totem', 'Browser tab title for the totem follow link page')),
  )

  useGuestLandingSession('qr')

  useEffect(() => {
    if (!user || !groupId || !totemId) return
    const runKey = `${user.uid}|${groupId}|${totemId}`
    if (resolvedFor.current === runKey) return
    resolvedFor.current = runKey
    let cancelled = false
    // The page navigates away on success, so the trace is recorded inline at
    // each outcome rather than through usePageLoadTrace.
    const resolved = (outcome: 'invalid' | 'no_route' | 'route' | 'error') =>
      recordTrace('follow_totem_resolve', traceStart.wall, performance.now() - traceStart.perf, { outcome })

    async function run() {
      try {
        const found = await loadTotem(groupId!, totemId!)
        if (cancelled) return
        if (!found) {
          resolved('invalid')
          setStatus('invalid')
          return
        }
        setTotem(found)

        if (user!.isAnonymous) {
          // Signing in usually links this session and keeps the UID. When it
          // can't (the account already exists), usePendingTotemFollow re-applies
          // the follow under the new UID.
          setPendingIntent('totemFollow', `${groupId}_${totemId}`)
        }
        // A follow that fails must not strand them on the invalid-link screen:
        // the code scanned fine and the route below is still worth showing.
        // runMutation has already surfaced the real reason in a toast.
        await followTotem(groupId!, totemId!, 'qr').catch(() => {})
        if (cancelled) return

        const eventId = await resolveLiveEvent(groupId!, totemId!)
        if (cancelled) return
        if (!eventId) {
          resolved('no_route')
          setStatus('no-route')
          return
        }
        // Two hops on purpose. A cold scan has no history behind it, so pushing
        // the route view straight onto the replaced resolver entry would leave
        // the back gesture with nowhere to go but out of the app. Seating the
        // schedule underneath gives back somewhere useful to land — which for a
        // stranger who just scanned a pole is the whole rest of the festival.
        resolved('route')
        navigate(`/live-festival/${eventId}/schedule`, { replace: true })
        navigate(`/live-festival/${eventId}/totem/${totemId}`, {
          state: { festivalDrillIn: true },
        })
      } catch (err) {
        if (cancelled) return
        resolved('error')
        toastError(
          fbt("Couldn't load totem", 'Toast error when resolving a totem share link fails'),
          classifyError(err),
        )
        setStatus('invalid')
      }
    }

    run()
    return () => { cancelled = true }
  }, [user, groupId, totemId, followTotem, loadTotem, resolveLiveEvent, navigate, traceStart])

  if (status === 'invalid') {
    return (
      <LinkLandingMessage
        title={
          <fbt desc="Heading when a totem share link is not valid">
            This totem link isn't valid
          </fbt>
        }
        body={
          <fbt desc="Body text suggesting what to do when a totem share link is invalid">
            The totem may have been removed. Ask the group for a fresh code.
          </fbt>
        }
        action={
          <Button variant="outline" size="sm" onClick={() => navigate('/', { replace: true })}>
            {backToHomeButtonLabel()}
          </Button>
        }
      />
    )
  }

  // Followed successfully, but the group hasn't marked any sets yet — likely a
  // QR printed and taped to the totem before the schedule was planned. Say so
  // rather than dropping them into an empty festival view.
  if (status === 'no-route' && totem) {
    return (
      <LinkLandingMessage
        media={
          <TotemAvatar
            emoji={totem.emoji}
            imageUrl={totem.imageUrl}
            borderStyle={totem.borderStyle}
            className="size-16 text-3xl"
          />
        }
        title={
          <fbt desc="Heading after following a totem that has no marked sets yet">
            You're following <fbt:param name="totemName">{totem.name}</fbt:param>
          </fbt>
        }
        body={
          <fbt desc="Body text after following a totem with no marked sets yet">
            We'll show its route as soon as the group marks some sets.
          </fbt>
        }
        action={
          <Button variant="outline" size="sm" onClick={() => navigate('/', { replace: true })}>
            <fbt desc="Button to browse the app after following a totem with no route yet">
              Explore Faro
            </fbt>
          </Button>
        }
      />
    )
  }

  return <LinkLandingSpinner label={fbt('Finding this totem…', 'Loading message while resolving a totem share link')} />
}
